import { createPixelSchema } from './createPixelSchema';
import {
  answered_ids,
  answered_indexes,
  answered_others,
  answers,
  answers_overall,
  collected_score,
  correct_answers_count,
  current_hash,
  current_hash_final,
  elapsed_time,
  external_url,
  file_is_uploaded,
  file_name,
  file_path,
  final_id,
  final_index,
  final_number,
  group_id,
  media_progress,
  media_question_type,
  previous_hash,
  previous_hash_final,
  question_deck_id,
  question_id,
  question_index,
  question_text,
  question_type,
  questions_count,
  set_id,
  time_expired,
  twork_id,
  user_id,
} from './properties';

// Значение версии равно дате релиза изменений в формате yyyy.MM.dd
export const PIXEL_VERSION = '2025.02.24';

export const welcomeClickSchema = createPixelSchema({
  event: 'welcome-click',
  description: 'Клик на начальном экране',
  version: PIXEL_VERSION,
});

export const quizStartSchema = createPixelSchema({
  event: 'start',
  description: 'Начато прохождение опроса',
  version: PIXEL_VERSION,
});

export const quizOverallSchema = createPixelSchema({
  event: 'overall',
  description: 'Агрегированные события прохождения (для интеграций)',
  version: PIXEL_VERSION,
  properties: {
    answered_ids,
    answers_overall,
    collected_score,
    questions_count,
    final_id,
    final_number,
    correct_answers_number: correct_answers_count,
  },
  required: ['answers_overall', 'final_id', 'final_number', 'correct_answers_number', 'collected_score'],
});

export const quizMediaProgressSchema = createPixelSchema({
  event: 'media-progress',
  description: 'Прогресс медиа файла',
  version: PIXEL_VERSION,
  properties: {
    media_progress,
    question_index,
    question_id,
    set_id,
    question_type: media_question_type,
    question_text,
  },
  required: ['question_index', 'question_id', 'question_text', 'question_type', 'media_progress'],
});

export const quizLoadSchema = createPixelSchema({
  event: 'load',
  description: 'Опрос загружен',
  version: PIXEL_VERSION,
});

export const quizFinishSchema = createPixelSchema({
  event: 'finish',
  description: 'Опрос завершен',
  version: PIXEL_VERSION,
  properties: {
    final_id,
    final_index,
    current_hash: current_hash_final,
    previous_hash: previous_hash_final,
    collected_score,
    correct_answers_count,
    user_id,
    twork_id,
  },
  required: ['final_id', 'final_index', 'collected_score', 'correct_answers_count'],
});

export const quizFinalClickSchema = createPixelSchema({
  event: 'final-click',
  description: 'Клик на финальном экране',
  version: PIXEL_VERSION,
  properties: {
    final_id,
    final_index,
    user_id,
    twork_id,
  },
  required: ['final_id', 'final_index'],
});

export const quizAnswerSchema = createPixelSchema({
  event: 'answer',
  description: 'Ответ на вопрос',
  version: PIXEL_VERSION,
  properties: {
    set_id,
    group_id,
    question_deck_id,
    question_id,
    question_index,
    question_text,
    question_type,
    answered_ids,
    answered_indexes,
    answered_others,
    current_hash,
    previous_hash,
    answers,
    collected_score,
    elapsed_time,
    time_expired,
    user_id,
    twork_id,
  },
  required: [
    'question_id',
    'question_index',
    'question_text',
    'question_type',
    'answered_ids',
    'answered_indexes',
    'answers',
    'collected_score',
    'elapsed_time',
    'time_expired',
    'current_hash',
  ],
});

export const quizUploadSchema = createPixelSchema({
  event: 'upload',
  description: 'Загрузка файла',
  version: PIXEL_VERSION,
  properties: {
    user_id,
    twork_id,
    file_name,
    file_path,
    file_is_uploaded,
  },
  required: ['file_is_uploaded', 'file_name', 'file_path'],
});

export const quizLinkSchema = createPixelSchema({
  event: 'quiz-link',
  description: 'Клик по внешней ссылке',
  version: PIXEL_VERSION,
  properties: {
    external_url,
    user_id,
    twork_id,
  },
  required: ['external_url'],
});

export const pixelsSchemaList = [
  welcomeClickSchema,
  quizStartSchema,
  quizMediaProgressSchema,
  quizOverallSchema,
  quizLoadSchema,
  quizFinishSchema,
  quizFinalClickSchema,
  quizAnswerSchema,
  quizUploadSchema,
  quizLinkSchema,
] as const;

type PixelSchema = (typeof pixelsSchemaList)[number];
export type PixelEvent = PixelSchema['properties']['event']['const'];
type SchemaByEvent<E extends PixelEvent, S extends PixelSchema = PixelSchema> = S extends unknown
  ? S['properties']['event']['const'] extends E
    ? S
    : never
  : never;

const schemaListToMap = <S extends PixelSchema>(
  schemaList: readonly S[],
): {
  [T in S['properties']['event']['const']]: SchemaByEvent<T>;
} => {
  return schemaList.reduce(
    (result, schema) => ({ ...result, [schema.properties.event.const]: schema }) as const,
    {} as { [T in S['properties']['event']['const']]: SchemaByEvent<T> },
  );
};

export const pixelsSchemaMap = schemaListToMap(pixelsSchemaList);

export const PIXEL_EVENTS = Object.keys(pixelsSchemaMap) as PixelEvent[];

export type PixelSchemaMap = typeof pixelsSchemaMap;
